"use client";

import { forwardRef, useState, type ComponentPropsWithoutRef, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Icon, type IconName } from "@/components/ui/icon";
import { Select } from "@/components/ui/select";
import type { InputVariantProps } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Sheet-form shell copied from the sequence settings sheet
 * (`features/sequences/components/detail/actions/sequence-settings/ui.tsx`):
 * a bordered card with an icon + heading strip, then stacked field rows.
 */
export function SectionCard({
  iconName,
  title,
  action,
  children,
  className,
}: {
  iconName: IconName;
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className="flex flex-col overflow-hidden rounded-lg border border-border">
      <div className="flex items-center gap-1.5 border-b border-border bg-contrast/2 px-4 py-3">
        <Icon name={iconName} size={16} className="shrink-0 text-contrast/70" aria-hidden="true" />
        <h3 className="text-sm font-medium text-heading">{title}</h3>
        {action ? <div className="ml-auto shrink-0">{action}</div> : null}
      </div>
      <div className={cn("flex flex-col gap-4 bg-surface-header p-4", className)}>
        {children}
      </div>
    </section>
  );
}

export function FieldRow({
  label,
  htmlFor,
  helper,
  error,
  disabled,
  children,
}: {
  label: string;
  htmlFor?: string;
  helper?: ReactNode;
  error?: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn("flex w-full flex-col gap-2", disabled && "opacity-50")}>
      <div className="flex flex-col gap-0.5">
        {/* A <label> only when it points at one control; a group of controls
            gets plain text so nothing claims a bogus `for` target. */}
        {htmlFor ? (
          <label htmlFor={htmlFor} className="text-sm font-medium text-heading">
            {label}
          </label>
        ) : (
          <span className="text-sm font-medium text-heading">{label}</span>
        )}
        {helper ? <p className="text-[13px] text-contrast/60">{helper}</p> : null}
      </div>
      {children}
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The app's "simple select" (see `/ui` → Select, and
 * `features/settings/components/users/invite-members.tsx`): a `Select.Trigger`
 * with a chevron `Select.Icon`, and a `Select.Content` list whose selected row
 * carries `Select.ItemLabel selected` plus a `Select.ItemCheck`. Wrapped once
 * here because the scorecards editor and fill form need six of them.
 */
export interface ScorecardSelectOption<T extends string> {
  value: T;
  label: string;
}

interface ScorecardSelectProps<T extends string> {
  /** Accessible name of the trigger — the control is not paired with a <label>. */
  label: string;
  value: T | "";
  options: ScorecardSelectOption<T>[];
  onChange: (value: T) => void;
  /** Trigger text while nothing is selected. */
  placeholder?: string;
  disabled?: boolean;
  size?: InputVariantProps["size"];
  variant?: InputVariantProps["variant"];
  className?: string;
  contentClassName?: string;
  id?: string;
  "aria-describedby"?: string;
}

export function ScorecardSelect<T extends string>({
  label,
  value,
  options,
  onChange,
  placeholder = "Choose…",
  disabled,
  size = "sm",
  variant,
  className,
  contentClassName,
  id,
  "aria-describedby": describedBy,
}: ScorecardSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Select.Root open={open} onOpenChange={setOpen} responsive={false}>
      <Select.Trigger
        id={id}
        aria-label={label}
        aria-describedby={describedBy}
        disabled={disabled}
        size={size}
        variant={variant}
        active={Boolean(selected)}
        className={className}
      >
        <Select.TriggerContent>
          {selected ? selected.label : placeholder}
        </Select.TriggerContent>
        <Select.Icon />
      </Select.Trigger>
      <Select.Content className={contentClassName}>
        <Select.List>
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <Select.Item
                key={option.value}
                selected={isSelected}
                onSelect={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <Select.ItemLabel selected={isSelected}>
                  {option.label}
                </Select.ItemLabel>
                {isSelected ? <Select.ItemCheck /> : null}
              </Select.Item>
            );
          })}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
}

/**
 * Drag grabber styled like the sequences step card
 * (`features/sequences/components/detail/steps/step-card.tsx`): a dot-grid
 * icon, `cursor-grab`, `touch-none` so the pointer sensor owns touch drags.
 */
export const DragHandle = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<"button"> & { label: string }
>(function DragHandle({ label, className, disabled, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      disabled={disabled}
      className={cn(
        "flex size-6 shrink-0 touch-none items-center justify-center rounded-md text-contrast/40 transition-colors",
        disabled
          ? "cursor-not-allowed opacity-40"
          : "cursor-grab hover:bg-contrast/6 hover:text-contrast/70 active:cursor-grabbing",
        className,
      )}
      {...props}
    >
      <Icon name="IconDotGrid2x3" size={16} aria-hidden="true" />
    </button>
  );
});

/** Ghost icon button for row actions — `aria-label` is required, never optional. */
export const RowIconButton = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<"button"> & { label: string; children: ReactNode }
>(function RowIconButton({ label, className, children, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      className={cn(
        "flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-contrast/50 transition-colors hover:bg-contrast/6 hover:text-heading disabled:pointer-events-none disabled:opacity-40",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});

/**
 * Where a scorecard type came from. Text always carries the meaning — the
 * variant only reinforces it (see the jobs list status badges).
 */
export function SourceBadge({ source }: { source: string }) {
  return (
    <Badge variant="outline-muted" size="sm" className="rounded-sm">
      <Icon
        name={source.startsWith("Library") ? "IconClipboard" : "IconBag"}
        size={12}
        aria-hidden="true"
      />
      {source}
    </Badge>
  );
}

export function ArchivedBadge() {
  return (
    <Badge
      variant="outline-muted"
      size="xs"
      className="rounded-sm border-border bg-contrast/4 text-contrast/50"
    >
      Archived
    </Badge>
  );
}

export function LibraryUpdateBadge() {
  return (
    <Badge variant="warning" size="xs" className="rounded-sm">
      Update available
    </Badge>
  );
}

/** "3 fields" / "1 field" — the sub-line under a scorecard type's name. */
export function fieldCountLabel(n: number): string {
  return `${n} ${n === 1 ? "field" : "fields"}`;
}

/** Short date for list cells, matching the jobs list ("Sep 15, 2026"). */
export function shortDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
